export const dimensionOrder = [
  "visibility",
  "governance",
  "optimization",
  "commitments",
  "architecture",
  "operatingModel",
] as const;

export type Dimension = (typeof dimensionOrder)[number];

export interface CompanyContext {
  companyName: string;
  respondentName: string;
  role: string;
  industry: string;
  annualAwsSpend: string;
  awsAccounts: string;
  primaryGoal: string;
  planningHorizon: string;
  estimatedMonthlyOpportunity: string;
  recommendationVolume: string;
}

export type AnswerMap = Record<string, string>;

export interface QuestionOption {
  value: string;
  label: string;
  description: string;
  score: number;
  signals: string[];
}

export interface Question {
  id: string;
  dimension: Dimension;
  prompt: string;
  help: string;
  options: QuestionOption[];
}

export interface DimensionResult {
  dimension: Dimension;
  label: string;
  score: number;
  answered: number;
  total: number;
  summary: string;
}

export interface Recommendation {
  id: string;
  dimension: Dimension;
  title: string;
  priority: number;
  impact: "Focused" | "Material" | "Transformational";
  horizon: "0-30 days" | "31-60 days" | "61-90 days";
  owner: string;
  why: string;
  evidence: string[];
  actions: string[];
  decisionRequired: string;
  firstDeliverable: string;
  successMeasure: string;
  customerInput: string;
  drivenSupport: string;
}

export interface StrategyInsight {
  title: string;
  body: string;
  tone: "strength" | "tension" | "opportunity";
  action: string;
  owner: string;
  timing: string;
}

export interface RoadmapPhase {
  label: "0-30 days" | "31-60 days" | "61-90 days";
  objective: string;
  actions: string[];
  outcome: string;
  owner: string;
  gate: string;
}

export type OpportunityLaneId = "commitments" | "graviton" | "rightsizing" | "hygiene";

export interface OpportunityLane {
  id: OpportunityLaneId;
  label: string;
  potential: "Primary" | "Secondary" | "Supporting" | "Monitor";
  potentialScore: number;
  readiness: "Ready to test" | "Needs validation" | "Not ready";
  readinessScore: number;
  valueShape: string;
  effort: string;
  thesis: string;
  decisionGate: string;
  evidence: string[];
  validation: string[];
  firstMove: string;
  treatment: "Analyze" | "Validate" | "Pilot" | "Automate" | "Group" | "Prepare" | "Monitor";
  decisionOwner: string;
  firstDeliverable: string;
  successMeasure: string;
  drivenMotion: string;
  measured: CostLaneSummary | null;
}

export interface CostGroupSummary {
  key: string;
  label: string;
  recommendationCount: number;
  monthlySavings: number;
  shareOfSavings: number;
}

export interface CostRecommendationHighlight {
  actionType: string;
  resourceType: string;
  region: string;
  accountId: string;
  monthlyCost: number;
  monthlySavings: number;
  savingsPercentage: number;
  implementationEffort: string;
  restartNeeded: boolean | null;
  rollbackPossible: boolean | null;
  currentSummary: string;
  recommendedSummary: string;
}

export interface CostLaneSummary {
  id: OpportunityLaneId;
  label: string;
  recommendationCount: number;
  monthlyCost: number;
  monthlySavings: number;
  annualizedSavings: number;
  shareOfSavings: number;
  restartRequiredCount: number;
  reversibleCount: number;
  topReversibleCount: number;
  topReversibleMonthlySavings: number;
  highEffortCount: number;
  topResourceTypes: string[];
}

export interface CostImportAnalysis {
  fileName: string;
  importedAt: string;
  rowCount: number;
  totalMonthlyCost: number;
  totalMonthlySavings: number;
  annualizedSavings: number;
  savingsRate: number | null;
  accountCount: number;
  regionCount: number;
  latestRefresh: string | null;
  lowEffortSavings: number;
  noRestartSavings: number;
  reversibleSavings: number;
  lowValueTailCount: number;
  lowValueTailSavings: number;
  topTenSavingsShare: number;
  lanes: CostLaneSummary[];
  actionSummaries: CostGroupSummary[];
  resourceSummaries: CostGroupSummary[];
  topRecommendations: CostRecommendationHighlight[];
  warnings: string[];
}

export interface ExecutiveDecision {
  label: "Priority 1" | "Priority 2" | "Portfolio rule";
  action: string;
  reason: string;
  owner: string;
  timing: string;
  deliverable: string;
  successGate: string;
}

export interface AssessmentResult {
  context: CompanyContext;
  dimensions: DimensionResult[];
  overallScore: number;
  maturityStage: "Reactive" | "Emerging" | "Defined" | "Managed" | "Adaptive";
  confidence: number;
  executiveHeadline: string;
  executiveDecisions: ExecutiveDecision[];
  executivePosition: string;
  portfolioThesis: string;
  portfolioFacts: string[];
  opportunityLanes: OpportunityLane[];
  primaryOpportunity: OpportunityLaneId;
  strengths: string[];
  recommendations: Recommendation[];
  insights: StrategyInsight[];
  roadmap: RoadmapPhase[];
  costAnalysis: CostImportAnalysis | null;
  generatedAt: string;
}

export const emptyContext: CompanyContext = {
  companyName: "",
  respondentName: "",
  role: "",
  industry: "",
  annualAwsSpend: "",
  awsAccounts: "",
  primaryGoal: "",
  planningHorizon: "",
  estimatedMonthlyOpportunity: "",
  recommendationVolume: "",
};
